import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { teamApi, PromptConfig } from '@/services/api';

const DEFAULT_KEYWORDS = ['priority', 'prioritize', 'urgent', 'urgency', 'p0', 'p1', 'critical', 'blocker', 'asap', 'roadmap', 'timeline', 'eta', 'when will', 'any plans', 'is there a plan', 'are there plans', 'feature request', 'planned for', 'on the roadmap'];

interface TeamLeadEscalationPanelProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

export default function TeamLeadEscalationPanel({ teamId, onNotify }: TeamLeadEscalationPanelProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Partial<PromptConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');

  const { data: promptConfig } = useQuery({
    queryKey: ['prompt-config', teamId],
    queryFn: () => teamApi.getPromptConfig(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  useEffect(() => {
    if (promptConfig) {
      setFormData({
        team_lead_name: promptConfig.team_lead_name,
        team_lead_email: promptConfig.team_lead_email,
        team_lead_tag: promptConfig.team_lead_tag,
        team_lead_google_chat_user_id: promptConfig.team_lead_google_chat_user_id,
        prioritization_keywords: promptConfig.prioritization_keywords,
      });
      setHasChanges(false);
    }
  }, [promptConfig]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<PromptConfig>) => teamApi.updatePromptConfig(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-config', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team-prompt-config', teamId] });
      onNotify('Team lead & escalation settings saved!', 'success');
      setHasChanges(false);
    },
    onError: (error: Error) => {
      onNotify(`Failed to save: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleChange = (field: keyof PromptConfig, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !formData.prioritization_keywords?.includes(newKeyword.trim().toLowerCase())) {
      const keywords = [...(formData.prioritization_keywords || []), newKeyword.trim().toLowerCase()];
      handleChange('prioritization_keywords', keywords);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    const keywords = (formData.prioritization_keywords || []).filter((k) => k !== keyword);
    handleChange('prioritization_keywords', keywords);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Configure the team lead who will be suggested for prioritization and escalation requests.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => updateMutation.mutate(formData)}
          disabled={!hasChanges || updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have unsaved changes.
        </Alert>
      )}

      {/* Team Lead Info */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Team Lead Contact
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Team Lead Name"
            fullWidth
            value={formData.team_lead_name || ''}
            onChange={(e) => handleChange('team_lead_name', e.target.value)}
            helperText="Display name shown in responses"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Team Lead Email"
            fullWidth
            type="email"
            value={formData.team_lead_email || ''}
            onChange={(e) => handleChange('team_lead_email', e.target.value)}
            helperText="Email address for contact"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Google Chat Tag (Fallback)"
            fullWidth
            value={formData.team_lead_tag || ''}
            onChange={(e) => handleChange('team_lead_tag', e.target.value)}
            helperText="Fallback @mention tag (e.g., john.doe)"
            placeholder="john.doe"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Google Chat User ID"
            fullWidth
            value={formData.team_lead_google_chat_user_id || ''}
            onChange={(e) => handleChange('team_lead_google_chat_user_id', e.target.value)}
            helperText="For clickable @mentions (e.g., users/123456789)"
            placeholder="users/123456789"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Prioritization Keywords */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Prioritization Keywords
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When users mention these keywords, the bot will suggest contacting the team lead.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {(formData.prioritization_keywords || []).map((keyword) => (
          <Chip
            key={keyword}
            label={keyword}
            onDelete={() => handleRemoveKeyword(keyword)}
            color="primary"
            variant="outlined"
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          label="Add Keyword"
          size="small"
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
          placeholder="e.g., escalate"
        />
        <Button variant="outlined" onClick={handleAddKeyword} startIcon={<AddIcon />}>
          Add
        </Button>
        <Button
          size="small"
          startIcon={<ResetIcon />}
          onClick={() => handleChange('prioritization_keywords', DEFAULT_KEYWORDS)}
        >
          Reset to Defaults
        </Button>
      </Box>
    </Box>
  );
}
